import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { tokens } from '../../theme/tokens';

// ---------------------------------------------------------------------------
// Smartsheet-style resource schedule: people rows, a time axis across the top,
// and per-assignment allocation bars. Bars are gray when <=100% utilized and
// red when over-allocated (>100%). Zoom by Day / Week / Month.
// ---------------------------------------------------------------------------

type Mode = 'D' | 'W' | 'M';

export interface ScheduleUser {
  id: string;
  name: string;
  subtitle?: string;
}

export interface ScheduleAssignment {
  id: string;
  userId: string;
  label: string; // project name
  role?: string;
  hoursAllocated: number;
  start: Date;
  end: Date;
}

const LEFT_W = 220;
const ROW_H = 44;
const HEADER_H = 54;
const COL_W: Record<Mode, number> = { D: 40, W: 108, M: 132 };

const MS_DAY = 86400000;
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const mondayOf = (d: Date) => { const x = startOfDay(d); const day = (x.getDay() + 6) % 7; return addDays(x, -day); };
const firstOfMonth = (d: Date) => { const x = startOfDay(d); x.setDate(1); return x; };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (d: Date) => `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
const isoWeek = (d: Date) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - day + 3);
  const first = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t.getTime() - first.getTime()) / MS_DAY - 3 + ((first.getUTCDay() + 6) % 7)) / 7);
};

const initials = (name: string) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || '?';

// Color encodes utilization: light→dark gray up to 100%, red when over.
const barStyle = (pct: number): { bg: string; fg: string } => {
  if (pct > 100) return { bg: tokens.color.danger, fg: '#fff' };
  if (pct <= 0) return { bg: '#EEF0F3', fg: tokens.color.textMuted };
  if (pct >= 100) return { bg: '#374151', fg: '#fff' };
  if (pct >= 80) return { bg: '#475569', fg: '#fff' };
  if (pct >= 50) return { bg: '#64748B', fg: '#fff' };
  return { bg: '#94A3B8', fg: '#fff' };
};

export const ResourceSchedule: React.FC<{
  users: ScheduleUser[];
  assignments: ScheduleAssignment[];
  /** Weekly capacity hours per userId (defaults to 40). */
  weeklyCapacity?: Record<string, number>;
}> = ({ users, assignments, weeklyCapacity }) => {
  const [mode, setMode] = useState<Mode>('W');
  const colW = COL_W[mode];

  // Visible date range, derived from assignments with padding.
  const { rangeStart, rangeEnd } = useMemo(() => {
    const dates = assignments.flatMap((a) => [a.start, a.end]).filter((d) => d && !isNaN(d.getTime()));
    let min = dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : new Date();
    let max = dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : new Date();
    min = addDays(min, -7);
    max = addDays(max, 21);
    if (max.getTime() - min.getTime() < 56 * MS_DAY) max = addDays(min, 84);
    return { rangeStart: min, rangeEnd: max };
  }, [assignments]);

  // Bucket boundaries (length = nBuckets + 1).
  const bucketStarts = useMemo(() => {
    const out: Date[] = [];
    let d =
      mode === 'W' ? mondayOf(rangeStart) : mode === 'M' ? firstOfMonth(rangeStart) : startOfDay(rangeStart);
    let guard = 0;
    while (d < rangeEnd && guard < 1000) {
      out.push(new Date(d));
      d = mode === 'W' ? addDays(d, 7) : mode === 'M' ? addMonths(d, 1) : addDays(d, 1);
      guard++;
    }
    out.push(new Date(d));
    return out;
  }, [rangeStart, rangeEnd, mode]);

  const nBuckets = Math.max(1, bucketStarts.length - 1);
  const totalW = nBuckets * colW;

  // Map a date to an x-offset that aligns with the (possibly uneven) buckets.
  const offsetFor = (date: Date): number => {
    const t = date.getTime();
    if (t <= bucketStarts[0].getTime()) return 0;
    if (t >= bucketStarts[nBuckets].getTime()) return totalW;
    let i = 0;
    while (i < nBuckets && bucketStarts[i + 1].getTime() <= t) i++;
    const span = bucketStarts[i + 1].getTime() - bucketStarts[i].getTime() || 1;
    const frac = (t - bucketStarts[i].getTime()) / span;
    return (i + frac) * colW;
  };

  const bucketLabel = (i: number) => {
    const s = bucketStarts[i];
    if (mode === 'M') return { top: `${MONTHS[s.getMonth()]} ${s.getFullYear()}`, sub: '' };
    if (mode === 'D') return { top: fmtDay(s), sub: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'][(s.getDay() + 6) % 7] };
    const e = addDays(bucketStarts[i + 1], -1);
    return { top: `${fmtDay(s)} – ${String(e.getDate()).padStart(2, '0')}`, sub: `Week ${isoWeek(s)}` };
  };

  const gridBg = `repeating-linear-gradient(90deg, ${tokens.color.border} 0, ${tokens.color.border} 1px, transparent 1px, transparent ${colW}px)`;

  const todayX = offsetFor(startOfDay(new Date()));
  const showToday = todayX > 0 && todayX < totalW;

  return (
    <Box>
      {/* Zoom control */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: tokens.color.textSecondary, fontSize: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#64748B' }} /> ≤ 100%
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: tokens.color.danger }} /> Over-allocated
          </Box>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontWeight: 600 } }}
        >
          <ToggleButton value="D">Day</ToggleButton>
          <ToggleButton value="W">Week</ToggleButton>
          <ToggleButton value="M">Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Scroll container */}
      <Box
        sx={{
          border: `1px solid ${tokens.color.border}`,
          borderRadius: `${tokens.radius.lg}px`,
          overflow: 'auto',
          maxHeight: 560,
          position: 'relative',
        }}
      >
        <Box sx={{ width: LEFT_W + totalW, minWidth: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3 }}>
            <Box
              sx={{
                width: LEFT_W,
                flexShrink: 0,
                height: HEADER_H,
                position: 'sticky',
                left: 0,
                zIndex: 4,
                bgcolor: tokens.color.surface,
                borderRight: `1px solid ${tokens.color.border}`,
                borderBottom: `1px solid ${tokens.color.border}`,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                fontWeight: 650,
                fontSize: 13,
              }}
            >
              Team member
            </Box>
            <Box sx={{ position: 'relative', width: totalW, height: HEADER_H, bgcolor: tokens.color.surfaceMuted, borderBottom: `1px solid ${tokens.color.border}` }}>
              {Array.from({ length: nBuckets }).map((_, i) => {
                const lab = bucketLabel(i);
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      left: i * colW,
                      top: 0,
                      width: colW,
                      height: '100%',
                      borderLeft: `1px solid ${tokens.color.border}`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: mode === 'M' ? 'flex-start' : 'center',
                      px: mode === 'M' ? 1 : 0.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.color.text, whiteSpace: 'nowrap' }}>
                      {lab.top}
                    </Typography>
                    {lab.sub ? (
                      <Typography sx={{ fontSize: 10.5, color: tokens.color.textMuted, whiteSpace: 'nowrap' }}>{lab.sub}</Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Rows */}
          {users.map((u) => {
            const cap = Math.max(1, weeklyCapacity?.[u.id] || 40);
            const rows = assignments.filter((a) => a.userId === u.id);
            return (
              <Box key={u.id} sx={{ display: 'flex', '&:hover .nx-name': { bgcolor: tokens.color.surfaceMuted } }}>
                <Box
                  className="nx-name"
                  sx={{
                    width: LEFT_W,
                    flexShrink: 0,
                    height: ROW_H,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    bgcolor: tokens.color.surface,
                    borderRight: `1px solid ${tokens.color.border}`,
                    borderBottom: `1px solid ${tokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                  }}
                >
                  <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 700, bgcolor: tokens.color.accentSoft, color: tokens.color.accent }}>
                    {initials(u.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} noWrap title={u.name}>
                      {u.name}
                    </Typography>
                    {u.subtitle ? (
                      <Typography sx={{ fontSize: 11, color: tokens.color.textMuted, lineHeight: 1.2 }} noWrap>
                        {u.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>

                <Box sx={{ position: 'relative', width: totalW, height: ROW_H, borderBottom: `1px solid ${tokens.color.border}`, backgroundImage: gridBg }}>
                  {showToday ? (
                    <Box sx={{ position: 'absolute', left: todayX, top: 0, bottom: 0, width: '2px', bgcolor: tokens.color.accent, opacity: 0.5 }} />
                  ) : null}
                  {rows.map((a) => {
                    const x = offsetFor(a.start);
                    const w = Math.max(6, offsetFor(a.end) - x);
                    const pct = Math.round((Number(a.hoursAllocated || 0) / cap) * 100);
                    const c = barStyle(pct);
                    return (
                      <Tooltip
                        key={a.id}
                        arrow
                        title={
                          <Box sx={{ fontSize: 12 }}>
                            <div style={{ fontWeight: 700 }}>{a.label}</div>
                            <div>{pct}% · {a.hoursAllocated}h / wk{a.role ? ` · ${a.role}` : ''}</div>
                            <div style={{ opacity: 0.8 }}>{fmtDay(a.start)} – {fmtDay(a.end)}</div>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: x + 2,
                            top: 7,
                            width: w - 4,
                            height: ROW_H - 14,
                            bgcolor: c.bg,
                            color: c.fg,
                            borderRadius: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            overflow: 'hidden',
                            cursor: 'default',
                          }}
                        >
                          {w > 30 ? (
                            <Typography sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{pct}%</Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}

          {users.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: tokens.color.textMuted, fontSize: 13 }}>
              No team members with assignments in this view.
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
